import { Box, Grid, IconButton, InputAdornment, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Check } from '@mui/icons-material';
import CustomInput, { typeTextField } from '../../../components/UI/CustomInput';
import logo from '@images/logo.png';
import bgLogin from '@images/login/knowledge.svg';
import { reset } from '../../../apis/auth';
import { setSnackbar } from '../../../store/common/common.reducer';
import { publicRoutes } from '../../../configs/routes';
import {
  checkPasswordLength,
  isContainsLowercase,
  validateDigit,
  validateSpecialChar,
} from '../../../utils/validation';
import CustomButton from '../../../components/UI/CustomButton';
import ResendCode from '../Otp/ResendCode';
import './password.scss';

const SetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const isMobile = useMediaQuery('(max-width:991px)');
  const [disabled, setDisabled] = useState<boolean>(true);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [type, setType] = useState<typeTextField>('password');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const msisdn = state ? state.msisdn : '-';

  async function handleSubmit(event: { preventDefault: () => void }) {
    event.preventDefault();
    setLoading(true);

    const mobileNumber = msisdn?.startsWith('0')
      ? `+98${msisdn.slice(1, msisdn.length)}`
      : `+98${msisdn}`;

    reset(mobileNumber, password, otp)
      .then(() => {
        dispatch(
          setSnackbar({
            openSnackbar: true,
            message: 'رمز عبور جدید شما با موفقیت ثبت شد، با نام کاربری و رمز عبور جدید وارد شوید.',
          }),
        );
        navigate(publicRoutes.login);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (
      password.length > 4 &&
      !validateSpecialChar(password) &&
      !validateDigit(password) &&
      !checkPasswordLength(password) &&
      isContainsLowercase(password) &&
      password === repeatPassword &&
      otp.length === 5
    )
      setDisabled(false);
    else setDisabled(true);
  }, [password, repeatPassword, otp]);

  const FormContent = (
    <>
      <form className='form' onSubmit={handleSubmit}>
        <Box className='logo'>
          <img src={logo} alt='' width={140} />
        </Box>

        <Typography color='text.main' fontSize='small' fontWeight='lighter' mt={1}>
          {`کد تایید ارسال شده به شماره ${
            msisdn?.startsWith('0') ? msisdn : `0${msisdn}`
          } را وارد کنید`}
        </Typography>

        <CustomInput
          label={otp ? 'کد تایید' : ''}
          placeholder='کد تایید'
          value={otp}
          maxLength={5}
          error={false}
          inputMode='numeric'
          onChangeHandler={(value) => setOtp(value)}
        />

        <ResendCode sendOtp={() => navigate(publicRoutes.forgetPassword)} />

        <Typography color='text.main' fontSize='small' fontWeight='lighter' mt={2}>
          لطفا رمزعبور جدید خود را وارد کنید.
        </Typography>

        <CustomInput
          label={password ? 'رمز عبور' : ''}
          placeholder='رمز عبور'
          value={password}
          type={type}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {type === 'password' ? (
                  <IconButton onClick={() => setType('text')}>
                    <VisibilityOffIcon sx={{ color: '#6A6E73' }} />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => setType('password')}>
                    <VisibilityIcon sx={{ color: '#6A6E73' }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          onChangeHandler={(value) => setPassword(value)}
        />

        <CustomInput
          label={repeatPassword ? ' تکرار رمز عبور' : ''}
          placeholder=' تکرار رمز عبور'
          value={repeatPassword}
          type={type}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                {type === 'password' ? (
                  <IconButton onClick={() => setType('text')}>
                    <VisibilityOffIcon sx={{ color: '#6A6E73' }} />
                  </IconButton>
                ) : (
                  <IconButton onClick={() => setType('password')}>
                    <VisibilityIcon sx={{ color: '#6A6E73' }} />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          onChangeHandler={(value) => setRepeatPassword(value)}
        />

        {password !== '' && (
          <>
            <Box className='passwordRule'>
              <Check
                color={password.length > 4 && isContainsLowercase(password) ? 'success' : 'action'}
                fontSize='small'
              />
              <Typography
                fontSize='x-small'
                mt={0.5}
                color={
                  password.length > 4 && isContainsLowercase(password)
                    ? 'success.main'
                    : 'text.main'
                }
              >
                شامل حروف بزرگ و کوچک انگلیسی
              </Typography>
            </Box>

            <Box className='passwordRule'>
              <Check
                color={validateSpecialChar(password) ? 'action' : 'success'}
                fontSize='small'
              />
              <Typography
                fontSize='x-small'
                mt={0.5}
                color={validateSpecialChar(password) ? 'text.main' : 'success.main'}
              >
                شامل علامت ( مثلا ! @ # $)
              </Typography>
            </Box>

            <Box className='passwordRule'>
              <Check
                color={checkPasswordLength(password) ? 'action' : 'success'}
                fontSize='small'
              />
              <Typography
                fontSize='x-small'
                mt={0.5}
                color={checkPasswordLength(password) ? 'text.main' : 'success.main'}
              >
                شامل حداقل ۸ کاراکتر
              </Typography>
            </Box>

            <Box className='passwordRule'>
              <Check color={validateDigit(password) ? 'action' : 'success'} fontSize='small' />
              <Typography
                fontSize='x-small'
                mt={0.5}
                color={validateDigit(password) ? 'text.main' : 'success.main'}
              >
                شامل عدد
              </Typography>
            </Box>
          </>
        )}

        <Box display='flex' mt={2} gap={1} width='100%'>
          <CustomButton
            fullWidth
            variant='contained'
            disabled={disabled || loading}
            loading={loading}
            type='submit'
          >
            <Typography>تایید</Typography>
          </CustomButton>
        </Box>
      </form>
    </>
  );

  const MobileContent = (
    <Box className='mobileContent'>
      <Box>
        <img src={bgLogin} alt='' className='img' />
      </Box>

      <Box className='mainContent'>{FormContent}</Box>
    </Box>
  );

  if (isMobile) return MobileContent;

  return (
    <Grid container className='grid' dir='ltr'>
      <Grid item xl={6} lg={6} md={12} xs={12} className='leftGrid'>
        <img src={bgLogin} alt='' className='img' />
      </Grid>
      <Grid item xl={6} lg={6} md={12} xs={12} dir='rtl' className='rightGrid'>
        {FormContent}
      </Grid>
    </Grid>
  );
};

export default SetPassword;
